'use client'

import Link from 'next/link'
import Image from 'next/image'
import { useState, useEffect } from 'react'

interface MenuItem {
  id: string
  title: string
  description: string
  icon: string
  href: string
  bgColor: string
}

interface SiteSettings {
  headerTitle: string
  mainTitle: string
  mainDescription: string
}

interface Notice {
  id: number
  content: string
}

export default function Home() {
  const [menuItems, setMenuItems] = useState<MenuItem[]>([])
  const [siteSettings, setSiteSettings] = useState<SiteSettings>({
    headerTitle: '학생 장학 도우미',
    mainTitle: '장학금 정보를 쉽고 빠르게',
    mainDescription: '필요한 서비스를 선택해주세요',
  })
  const [notices, setNotices] = useState<Notice[]>([
    { id: 1, content: '장학금 신청 기간을 꼭 확인해주세요' },
    { id: 2, content: '정확한 계산을 위해 최신 정보를 입력해주세요' },
    { id: 3, content: '문의사항은 학생지원팀으로 연락주세요' },
  ])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchMenuSettings()
    fetchSiteSettings()
    fetchNotices()
  }, [])

  const fetchSiteSettings = async () => {
    try {
      const response = await fetch('/api/site-settings')
      const settings = await response.json()
      setSiteSettings(settings)
    } catch (error) {
      console.error('사이트 설정 로드 실패:', error)
    }
  }

  const fetchNotices = async () => {
    try {
      const response = await fetch('/api/notices')
      const data = await response.json()
      if (data.notices && Array.isArray(data.notices)) {
        setNotices(data.notices)
      }
    } catch (error) {
      console.error('공지사항 로드 실패:', error)
    }
  }

  // 마크다운 스타일 링크를 HTML로 변환
  const renderNoticeContent = (content: string) => {
    // [텍스트](URL) 형식을 <a> 태그로 변환
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g
    const parts: (string | JSX.Element)[] = []
    let lastIndex = 0
    let match

    while ((match = linkRegex.exec(content)) !== null) {
      // 링크 앞의 텍스트 추가
      if (match.index > lastIndex) {
        parts.push(content.substring(lastIndex, match.index))
      }
      
      // 링크 추가
      const linkText = match[1]
      const linkUrl = match[2]
      parts.push(
        <a
          key={match.index}
          href={linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline font-semibold"
        >
          {linkText}
        </a>
      )
      
      lastIndex = match.index + match[0].length
    }
    
    // 남은 텍스트 추가
    if (lastIndex < content.length) {
      parts.push(content.substring(lastIndex))
    }
    
    return parts.length > 0 ? parts : content
  }

  const fetchMenuSettings = async () => {
    try {
      const response = await fetch('/api/menu-settings')
      const result = await response.json()
      
      const items = result.data.map((item: any) => ({
        ...item,
        href: getHrefForId(item.id),
        bgColor: getBgColorForId(item.id),
      }))
      
      setMenuItems(items)
    } catch (error) {
      console.error('메뉴 설정 로드 실패:', error)
      // 기본값 설정
      setMenuItems([
        {
          id: 'campus',
          title: '교내 장학금',
          description: '학교에서 제공하는 다양한 장학금 정보를 확인하세요',
          href: '/scholarships/campus',
          icon: '🎓',
          bgColor: 'bg-gradient-to-br from-pastel-blue-100 to-pastel-blue-200',
        },
        {
          id: 'calculator',
          title: '등록금성 장학금 수혜범위 계산',
          description: '등록금 장학금 수혜 가능 범위를 계산해보세요',
          href: '/scholarships/tuition-calculator',
          icon: '🧮',
          bgColor: 'bg-gradient-to-br from-pastel-green-100 to-pastel-green-200',
        },
        {
          id: 'living',
          title: '생활비성 장학금',
          description: '생활비 지원 장학금 정보를 확인하세요',
          href: '/scholarships/living-expenses',
          icon: '💰',
          bgColor: 'bg-gradient-to-br from-pastel-purple-100 to-pastel-purple-200',
        },
      ])
    } finally {
      setLoading(false)
    }
  }

  const getHrefForId = (id: string) => {
    switch (id) {
      case 'campus': return '/scholarships/campus'
      case 'calculator': return '/scholarships/tuition-calculator'
      case 'living': return '/scholarships/living-expenses'
      default: return '/'
    }
  }

  const getBgColorForId = (id: string) => {
    switch (id) {
      case 'campus': return 'bg-gradient-to-br from-pastel-blue-100 to-pastel-blue-200'
      case 'calculator': return 'bg-gradient-to-br from-pastel-green-100 to-pastel-green-200'
      case 'living': return 'bg-gradient-to-br from-pastel-purple-100 to-pastel-purple-200'
      default: return 'bg-gradient-to-br from-gray-100 to-gray-200'
    }
  }

  const getImageForId = (id: string) => {
    switch (id) {
      case 'campus': return { src: '/suho.png', text: 'suho' }
      case 'calculator': return { src: '/cau.png', text: 'cau' }
      case 'living': return { src: '/suya.png', text: 'suya' }
      default: return null
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-blue-50 to-cyan-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-pastel-blue-300 to-pastel-purple-300 bg-clip-text text-transparent">{siteSettings.headerTitle}</h1>
            <Link 
              href="/admin/login"
              className="px-4 py-2 bg-purple-200 text-purple-800 rounded-lg hover:bg-purple-300 transition-all shadow-sm hover:shadow-md font-medium"
            >
              관리자
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-800 mb-4">
            {siteSettings.mainTitle}
          </h2>
          <p className="text-xl text-gray-600 whitespace-pre-line">
            {siteSettings.mainDescription}
          </p>
        </div>

        {/* Menu Cards */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">로딩 중...</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-8">
            {menuItems.map((item) => {
              const imageData = getImageForId(item.id)
              return (
                <Link 
                  key={item.id}
                  href={item.href}
                  className="group"
                >
                  <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 h-full border border-white/50">
                    {/* 이미지 섹션 */}
                    <div className={`${item.bgColor} w-full h-48 rounded-xl flex items-center justify-center mb-6 group-hover:scale-105 transition-transform relative overflow-hidden`}>
                      {imageData && (
                        <div className="relative w-40 h-40">
                          <Image
                            src={imageData.src}
                            alt={item.title}
                            fill
                            className="object-contain"
                          />
                        </div>
                      )}
                    </div>
                    
                    <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center whitespace-pre-line">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed whitespace-pre-line text-center">
                      {item.description}
                    </p>
                    <div className="mt-6 flex items-center text-pastel-blue-400 font-semibold">
                      자세히 보기
                      <svg className="w-5 h-5 ml-2 group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </Link>
              )
            })}
          </div>
        )}

        {/* Info Section */}
        <div className="mt-16 bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg p-8 border border-white/50">
          <h3 className="text-2xl font-bold text-gray-800 mb-4">
            📢 공지사항
          </h3>
          <ul className="space-y-3 text-gray-600">
            {notices.map((notice) => (
              <li key={notice.id} className="flex items-start">
                <span className="text-blue-400 mr-2">•</span>
                <span className="whitespace-pre-line">{renderNoticeContent(notice.content)}</span>
              </li>
            ))}
          </ul>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-sky-100 to-blue-100 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <p className="text-gray-600">
              © 2025 교내장학금 안내 도우미. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}



